'use client';

import React, { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useAuth } from '@/contexts/AuthContext';
import { useCompany } from '@/contexts/CompanyContext';
import AppLayout from '@/components/AppLayout';
import DashboardBentoGrid from './components/DashboardBentoGrid';
import DashboardCharts from './components/DashboardCharts';
import DashboardActivity from './components/DashboardActivity';
import DashboardHeader from './components/DashboardHeader';
import { Loader2 } from 'lucide-react';
import { createClient } from '@/lib/supabase/client';

export default function DashboardPage() {
  const router = useRouter();
  const { user, loading: authLoading } = useAuth();
  const { companies, activeCompany, loading: companyLoading } = useCompany();

  useEffect(() => {
    if (authLoading || companyLoading) return;

    if (!user) {
      router?.replace('/sign-up-login-screen');
      return;
    }

    // Check role — platform_admin must go to /nova-admin
    const supabase = createClient();
    supabase?.from('user_profiles')?.select('role')?.eq('id', user?.id)?.single()?.then(({ data }) => {
        if (data?.role === 'platform_admin') {
          router?.replace('/nova-admin');
          return;
        }

        // Customer portal routing
        if (!activeCompany) {
          if (companies?.length === 0) {
            router?.replace('/sign-up-login-screen');
          } else {
            router?.replace('/select-company');
          }
        }
      });
  }, [user, authLoading, companyLoading, activeCompany, companies, router]);

  if (authLoading || companyLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 size={32} className="animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">Loading NovaInvoice…</p>
        </div>
      </div>
    );
  }

  if (!user || !activeCompany) return null;

  return (
    <AppLayout activePath="/dashboard">
      <div className="space-y-6 lg:space-y-8 fade-in">
        <DashboardHeader />
        <DashboardBentoGrid />
        <DashboardCharts />
        <DashboardActivity />
      </div>
    </AppLayout>
  );
}